import { useMutation, useQueryClient } from "@tanstack/react-query";
import { isWhitespace } from "src/components";
import { configuration } from "../../../configuration";
import { useSignalR } from "../../../hooks";
import { EditIssueDecisionDto, FetchError, GetIssuesDto, IssueDto, UserDto } from "../../types";
import { useAccessToken } from "../../use-access-token";
import { queryKeys } from "../queryKeys";
import { optimisticallyUpdateQueryData } from "../utilities";

type Variables = {
  issueGuid: string;
  decision: string;
  teamSlug?: string;
  createdByUserId: string;
  modifiedByUser: UserDto;
};

const useEditIssueDecisionRequest = () => {
  const { getAccessToken } = useAccessToken();

  async function editIssueDecisionRequest(variables: Variables) {
    const accessToken = await getAccessToken();
    const body: EditIssueDecisionDto = {
      decision: variables.decision,
    };

    const response = await fetch(`${configuration.apiRootUrl}/issues/${variables.issueGuid}/decision`, {
      headers: {
        Authorization: `Bearer ${accessToken}`,
        "Content-Type": "application/json",
      },
      method: "put",
      body: JSON.stringify(body),
    });

    if (!response.ok) {
      throw new FetchError(response);
    }
  }

  return { editIssueDecisionRequest };
};

const useEditIssueDecision = () => {
  const { editIssueDecisionRequest } = useEditIssueDecisionRequest();
  const queryClient = useQueryClient();
  const signalR = useSignalR();

  return useMutation({
    mutationFn: editIssueDecisionRequest,
    onMutate: async (variables) => {
      let cachedTeamIssuesData: GetIssuesDto | undefined;

      if (variables.teamSlug) {
        cachedTeamIssuesData = await optimisticallyUpdateQueryData<GetIssuesDto>(
          queryClient,
          queryKeys.issues.getIssues(variables.teamSlug, undefined),
          (cachedData) => ({
            ...cachedData,
            issues: cachedData.issues.map((issue) =>
              issue.guid === variables.issueGuid
                ? {
                    ...issue,
                    hasDecision: !isWhitespace(variables.decision),
                  }
                : issue,
            ),
          }),
        );
      }

      const cachedUserIssuesData = await optimisticallyUpdateQueryData<GetIssuesDto>(
        queryClient,
        queryKeys.issues.getIssues(undefined, variables.createdByUserId),
        (cachedData) => ({
          ...cachedData,
          issues: cachedData.issues.map((issue) =>
            issue.guid === variables.issueGuid
              ? {
                  ...issue,
                  hasDecision: !isWhitespace(variables.decision),
                }
              : issue,
          ),
        }),
      );

      const cachedIssueData = await optimisticallyUpdateQueryData<IssueDto>(
        queryClient,
        queryKeys.issues.getIssueDetail(variables.issueGuid),
        (cachedData) => ({
          ...cachedData,
          decision: {
            value: variables.decision,
            modifiedByUser: variables.modifiedByUser,
            modifiedDateTimeUtc: new Date().toISOString(),
          },
        }),
      );

      return { cachedTeamIssuesData, cachedUserIssuesData, cachedIssueData };
    },
    onError: (_error, variables, context) => {
      if (variables.teamSlug) {
        queryClient.setQueryData(
          queryKeys.issues.getIssues(variables.teamSlug, undefined),
          context?.cachedTeamIssuesData,
        );
      }

      queryClient.setQueryData(
        queryKeys.issues.getIssues(undefined, variables.createdByUserId),
        context?.cachedUserIssuesData,
      );
      queryClient.setQueryData(queryKeys.issues.getIssueDetail(variables.issueGuid), context?.cachedIssueData);
    },
    onSuccess: (_data, variables) => {
      if (variables.teamSlug) {
        signalR.sendRefetchTeamIssues(variables.teamSlug);
      }

      signalR.sendRefetchUserIssues(variables.createdByUserId);
      signalR.sendRefetchIssue(variables.issueGuid);
      signalR.sendRefetchComments(variables.issueGuid);
    },
    onSettled: (_data, _error, variables) => {
      if (variables.teamSlug) {
        queryClient.invalidateQueries(queryKeys.issues.getIssues(variables.teamSlug, undefined));
      }

      queryClient.invalidateQueries(queryKeys.issues.getIssues(undefined, variables.createdByUserId));
      queryClient.invalidateQueries(queryKeys.issues.getIssueDetail(variables.issueGuid));
      queryClient.invalidateQueries(queryKeys.issues.getIssueFeed(variables.issueGuid));
    },
  });
};

export { useEditIssueDecision };
